import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import Layout from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { toast } from 'sonner';
import { formatPhoneDisplay } from '@/utils/phoneUtils';
import { Users, QrCode, CheckCircle, XCircle, Clock, Home, Calendar, MoreVertical, UserCheck, UserX, UserMinus, Download, RefreshCw, Plus, MapPin, AlertCircle, Copy } from 'lucide-react';
import QRCodeDisplay from 'react-qr-code';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BrokerDashboard = () => {
  const navigate = useNavigate();
  const { token, agent } = useAuth();
  const [agents, setAgents] = useState([]);
  const [joinRequests, setJoinRequests] = useState([]);
  const [qrCode, setQrCode] = useState(null);
  const [showQR, setShowQR] = useState(false);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  // Force pending brokers to the my-openhouses tab
  useEffect(() => {
    if (agent?.status === 'pending') {
      setActiveTab('my-openhouses');
    }
  }, [agent?.status]);
  const [myEvents, setMyEvents] = useState([]);
  const [eventStats, setEventStats] = useState({ totalEvents: 0, totalVisitors: 0, activeEvents: 0 });

  useEffect(() => {
    if (agent?.role === 'broker') {
      fetchData();
      fetchMyEvents();
    }
  }, [agent]);

  const fetchMyEvents = async () => {
    try {
      const response = await axios.get(`${API}/events`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMyEvents(response.data);
      
      const totalVisitors = response.data.reduce((sum, event) => sum + event.visitor_count, 0);
      const activeEvents = response.data.filter(e => e.status === 'active').length;
      setEventStats({
        totalEvents: response.data.length,
        totalVisitors,
        activeEvents
      });
    } catch (error) {
      toast.error('Failed to fetch your open houses');
    }
  };

  const fetchData = async () => {
    try {
      const [agentsRes, requestsRes, qrRes] = await Promise.all([
        axios.get(`${API}/broker/agents`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/broker/join-requests`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/broker/qr`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      setAgents(agentsRes.data);
      setJoinRequests(requestsRes.data.filter(r => r.status === 'pending'));
      setQrCode(qrRes.data);
    } catch (error) {
      toast.error('Failed to fetch broker data');
    } finally {
      setLoading(false);
    }
  };

  const handleRequest = async (requestId, action) => {
    try {
      await axios.post(
        `${API}/broker/join-requests/${requestId}/action`,
        { action },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(action === 'approve' ? 'Agent approved!' : 'Request rejected');
      fetchData();
    } catch (error) {
      toast.error('Failed to process request');
    }
  };

  const handleStatusChange = async (agentId, newStatus) => {
    try {
      await axios.put(
        `${API}/broker/agents/${agentId}/status`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Agent status updated to ${newStatus}`);
      fetchData();
    } catch (error) {
      toast.error('Failed to update agent status');
    }
  };

  const regenerateQRCode = async () => {
    try {
      const response = await axios.post(
        `${API}/broker/qr/regenerate`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setQrCode(response.data);
      toast.success('QR Code regenerated successfully!');
    } catch (error) {
      toast.error('Failed to regenerate QR code');
    }
  };

  const downloadQRCode = () => {
    if (!qrCode) return;
    
    // Create a canvas element
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    
    // Create an image from the base64 string
    const img = new Image();
    img.src = `data:image/png;base64,${qrCode.qr_code}`;
    
    img.onload = () => {
      canvas.width = img.width;
      canvas.height = img.height;
      ctx.drawImage(img, 0, 0);
      
      // Convert canvas to blob and download
      canvas.toBlob((blob) => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `broker-qr-code-${agent?.name?.replace(/\s+/g, '-') || 'invite'}.png`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        toast.success('QR Code downloaded!');
      });
    };
  };

  if (agent?.role !== 'broker') {
    return (
      <Layout>
        <div className="max-w-7xl mx-auto text-center py-12">
          <p className="text-lg text-stone-600">This page is only accessible to brokers</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#1A4D2E] mb-2" style={{fontFamily: '"Playfair Display", serif'}}>
            Broker Dashboard
          </h1>
          <p className="text-stone-600">Manage your team and open houses</p>
        </div>

        {/* Approval Pending Banner */}
        {agent?.status === 'pending' && (
          <Alert className="mb-8 border-2 border-yellow-500 bg-yellow-50">
            <AlertCircle className="h-5 w-5 text-yellow-600" />
            <AlertTitle className="text-yellow-800 font-bold text-lg">Approval Pending</AlertTitle>
            <AlertDescription className="text-yellow-700 mt-2">
              Your broker account is awaiting administrator approval. Once approved, you'll be able to:
              <ul className="list-disc list-inside mt-2 ml-2 space-y-1">
                <li>Add and manage properties</li>
                <li>Create open house events</li>
                <li>Invite and manage agents</li>
                <li>Access all broker features</li>
              </ul>
              <p className="mt-3 font-semibold">
                Please contact the administrator if you have any questions.
              </p>
            </AlertDescription>
          </Alert>
        )}

        {/* Tab Navigation */}
        <div className="mb-8 border-b border-stone-200">
          <div className="flex gap-6">
            {agent?.status !== 'pending' && (
              <button
                onClick={() => setActiveTab('overview')}
                data-testid="tab-team-overview"
                className={`pb-4 px-2 text-base font-medium transition-colors relative ${
                  activeTab === 'overview'
                    ? 'text-[#1A4D2E] border-b-2 border-[#1A4D2E]'
                    : 'text-stone-500 hover:text-stone-700'
                }`}
              >
                Team Overview
              </button>
            )}
            <button
              onClick={() => setActiveTab('my-openhouses')}
              data-testid="tab-my-openhouses"
              className={`pb-4 px-2 text-base font-medium transition-colors relative ${
                activeTab === 'my-openhouses'
                  ? 'text-[#1A4D2E] border-b-2 border-[#1A4D2E]'
                  : 'text-stone-500 hover:text-stone-700'
              }`}
            >
              My Open Houses
            </button>
          </div>
        </div>

        {/* Tab Content */}
        {activeTab === 'overview' && agent?.status !== 'pending' && (
          <div>

        {/* Stats */}
        <div className={`grid grid-cols-1 ${agent?.show_open_house_requests ? 'md:grid-cols-3' : 'md:grid-cols-2'} gap-6 mb-8`}>
          {agent?.show_open_house_requests && (
            <Card 
              className="bg-white p-8 rounded-2xl card-shadow cursor-pointer hover:card-shadow-hover transition-all"
              onClick={() => navigate('/broker/requests')}
            >
              <div className="flex items-center gap-4">
                <div className="p-4 rounded-full bg-[#1A4D2E]/10">
                  <Home className="text-[#1A4D2E]" size={28} />
                </div>
                <div>
                  <p className="text-sm font-semibold uppercase tracking-wider text-stone-500">Open House Requests</p>
                  <p className="text-lg sm:text-2xl font-bold text-[#1A4D2E]" style={{fontFamily: '"Playfair Display", serif'}}>Manage</p>
                </div>
              </div>
            </Card>
          )}

          <Card 
            className="bg-white p-8 rounded-2xl card-shadow cursor-pointer hover:card-shadow-hover transition-all"
            onClick={() => navigate('/broker/events')}
          >
            <div className="flex items-center gap-4">
              <div className="p-4 rounded-full bg-blue-100">
                <Calendar className="text-blue-600" size={28} />
              </div>
              <div>
                <p className="text-sm font-semibold uppercase tracking-wider text-stone-500">Assigned Open Houses</p>
                <p className="text-lg sm:text-2xl font-bold text-[#1A4D2E]" style={{fontFamily: '"Playfair Display", serif'}}>View All</p>
              </div>
            </div>
          </Card>

          {(agent?.status === 'approved' || agent?.status === 'active') && (
          <Card className="bg-white p-8 rounded-2xl card-shadow">
            <Button
              onClick={() => setShowQR(!showQR)}
              className="w-full bg-[#1A4D2E] text-white rounded-full h-full hover:bg-[#143D24]"
              data-testid="show-broker-qr"
            >
              <QrCode className="mr-2" size={24} />
              {showQR ? 'Hide' : 'Show'} Join QR Code
            </Button>
          </Card>
          )}
        </div>

        {/* QR Code Display */}
        {showQR && qrCode && (
          <Card className="bg-white p-8 rounded-2xl card-shadow mb-8 text-center" data-testid="broker-qr-display">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-2xl font-bold text-[#1A4D2E]" style={{fontFamily: '"Playfair Display", serif'}}>
                Agent Join QR Code
              </h2>
              <div className="flex gap-2">
                <Button
                  onClick={regenerateQRCode}
                  variant="outline"
                  className="border-[#1A4D2E] text-[#1A4D2E] hover:bg-[#1A4D2E] hover:text-white"
                  size="sm"
                >
                  <RefreshCw size={16} className="mr-2" />
                  Regenerate
                </Button>
                <Button
                  onClick={downloadQRCode}
                  className="bg-[#1A4D2E] text-white hover:bg-[#143D24]"
                  size="sm"
                >
                  <Download size={16} className="mr-2" />
                  Download
                </Button>
                <Button
                  onClick={() => { navigator.clipboard.writeText(qrCode.url); toast.success('URL copied to clipboard'); }}
                  variant="outline"
                  className="border-stone-300 text-stone-600 hover:bg-stone-100"
                  size="sm"
                  data-testid="copy-join-url-btn"
                >
                  <Copy size={16} className="mr-2" />
                  Copy URL
                </Button>
              </div>
            </div>
            <p className="text-stone-600 mb-6">Agents can scan this code to request to join your brokerage</p>
            <div className="inline-block bg-white p-6 rounded-xl border-2 border-[#1A4D2E]">
              <QRCodeDisplay value={qrCode.url} size={250} />
            </div>
            <p className="text-sm text-stone-500 mt-4 break-all">{qrCode.url}</p>
          </Card>
        )}

        {/* Pending Join Requests */}
        {joinRequests.length > 0 && (
          <Card className="bg-white p-8 rounded-2xl card-shadow mb-8">
            <h2 className="text-2xl font-bold text-[#1A4D2E] mb-6" style={{fontFamily: '"Playfair Display", serif'}}>
              Pending Join Requests
            </h2>
            <div className="space-y-4">
              {joinRequests.map((request) => (
                <Card key={request.id} className="p-6 border border-orange-200 bg-orange-50" data-testid={`join-request-${request.id}`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-xl font-bold text-[#1A4D2E]" style={{fontFamily: '"Playfair Display", serif'}}>
                        {request.agent_name}
                      </h3>
                      <p className="text-sm text-stone-600">{request.agent_email}</p>
                      <p className="text-xs text-stone-500 mt-1">
                        Requested: {new Date(request.created_at).toLocaleString()}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        onClick={() => handleRequest(request.id, 'approve')}
                        className="bg-green-600 text-white rounded-full px-6 hover:bg-green-700"
                        data-testid={`approve-${request.id}`}
                      >
                        <CheckCircle size={16} className="mr-1" />
                        Approve
                      </Button>
                      <Button
                        onClick={() => handleRequest(request.id, 'reject')}
                        variant="outline"
                        className="border-2 border-red-300 text-red-600 rounded-full px-6 hover:bg-red-50"
                        data-testid={`reject-${request.id}`}
                      >
                        <XCircle size={16} className="mr-1" />
                        Reject
                      </Button>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </Card>
        )}

        {/* Active Agents */}
        <Card className="bg-white p-8 rounded-2xl card-shadow">
          <h2 className="text-2xl font-bold text-[#1A4D2E] mb-6" style={{fontFamily: '"Playfair Display", serif'}}>
            Your Agents
          </h2>
          
          {agents.length === 0 ? (
            <div className="text-center py-12">
              <Users className="mx-auto mb-4 text-stone-300" size={48} />
              <p className="text-lg text-stone-600">No agents yet</p>
              <p className="text-sm text-stone-500 mt-2">Share your QR code with agents to get started</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {agents.map((ag) => (
                <Card 
                  key={ag.id} 
                  className="p-6 border border-stone-200 relative cursor-pointer hover:border-[#1A4D2E] hover:shadow-lg transition-all" 
                  data-testid={`agent-card-${ag.id}`}
                  onClick={() => navigate(`/broker/agents/${ag.id}`)}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className="p-3 rounded-full bg-[#1A4D2E]/10">
                        <Users className="text-[#1A4D2E]" size={20} />
                      </div>
                      <div>
                        <h3 className="font-bold text-[#1A4D2E]">{ag.name}</h3>
                        <p className="text-xs text-stone-500">{ag.email}</p>
                      </div>
                    </div>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="h-8 w-8 p-0"
                          data-testid={`agent-menu-${ag.id}`}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <MoreVertical size={16} />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                        <DropdownMenuItem 
                          onClick={() => handleStatusChange(ag.id, 'approved')}
                          disabled={ag.status === 'approved' || ag.status === 'active'}
                          data-testid={`status-approved-${ag.id}`}
                        >
                          <UserCheck size={16} className="mr-2" />
                          Set as Approved
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                          onClick={() => handleStatusChange(ag.id, 'inactive')}
                          disabled={ag.status === 'inactive'}
                          data-testid={`status-inactive-${ag.id}`}
                        >
                          <UserMinus size={16} className="mr-2" />
                          Set as Inactive
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                          onClick={() => handleStatusChange(ag.id, 'rejected')}
                          disabled={ag.status === 'rejected'}
                          className="text-red-600"
                          data-testid={`status-rejected-${ag.id}`}
                        >
                          <UserX size={16} className="mr-2" />
                          Set as Rejected
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                  {ag.phone && (
                    <p className="text-sm text-stone-600 mb-2">{formatPhoneDisplay(ag.phone)}</p>
                  )}
                  <div className="mt-3 pt-3 border-t border-stone-100">
                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${
                      ag.status === 'approved' || ag.status === 'active' 
                        ? 'bg-green-100 text-green-800' 
                        : ag.status === 'rejected' 
                        ? 'bg-red-100 text-red-800' 
                        : ag.status === 'inactive'
                        ? 'bg-gray-100 text-gray-800'
                        : 'bg-orange-100 text-orange-800'
                    }`}>
                      {ag.status === 'approved' || ag.status === 'active' ? '✓ Active' : 
                       ag.status === 'rejected' ? '✗ Rejected' : 
                       ag.status === 'inactive' ? '⊗ Inactive' : 
                       '⏳ Pending'}
                    </span>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </Card>
          </div>
        )}

        {/* My Open Houses Tab */}
        {activeTab === 'my-openhouses' && (
          <div>
            {/* Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              <Card className="bg-white p-8 rounded-2xl card-shadow hover:-translate-y-1 transition-transform duration-300">
                <div className="flex items-center gap-4">
                  <div className="p-4 rounded-full bg-[#1A4D2E]/10">
                    <Home className="text-[#1A4D2E]" size={28} />
                  </div>
                  <div>
                    <p className="text-sm font-semibold uppercase tracking-wider text-stone-500">Total Open Houses</p>
                    <p className="text-2xl sm:text-4xl font-bold text-[#1A4D2E]" style={{fontFamily: '"Playfair Display", serif'}}>{eventStats.totalEvents}</p>
                  </div>
                </div>
              </Card>

              <Card className="bg-white p-8 rounded-2xl card-shadow hover:-translate-y-1 transition-transform duration-300">
                <div className="flex items-center gap-4">
                  <div className="p-4 rounded-full bg-[#D4A373]/10">
                    <Users className="text-[#D4A373]" size={28} />
                  </div>
                  <div>
                    <p className="text-sm font-semibold uppercase tracking-wider text-stone-500">Total Visitors</p>
                    <p className="text-2xl sm:text-4xl font-bold text-[#1A4D2E]" style={{fontFamily: '"Playfair Display", serif'}}>{eventStats.totalVisitors}</p>
                  </div>
                </div>
              </Card>

              <Card className="bg-white p-8 rounded-2xl card-shadow hover:-translate-y-1 transition-transform duration-300">
                <div className="flex items-center gap-4">
                  <div className="p-4 rounded-full bg-green-100">
                    <Calendar className="text-green-600" size={28} />
                  </div>
                  <div>
                    <p className="text-sm font-semibold uppercase tracking-wider text-stone-500">Active Open Houses</p>
                    <p className="text-2xl sm:text-4xl font-bold text-[#1A4D2E]" style={{fontFamily: '"Playfair Display", serif'}}>{eventStats.activeEvents}</p>
                  </div>
                </div>
              </Card>
            </div>

            {/* Action Buttons */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
              <Card 
                className={`bg-white p-6 rounded-2xl card-shadow transition-all ${
                  agent?.status === 'pending' 
                    ? 'opacity-50 cursor-not-allowed' 
                    : 'cursor-pointer hover:card-shadow-hover'
                }`}
                onClick={() => agent?.status !== 'pending' && navigate('/broker/properties')}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-3 rounded-full bg-[#1A4D2E]/10">
                      <MapPin className="text-[#1A4D2E]" size={24} />
                    </div>
                    <div>
                      <p className="text-sm font-semibold uppercase tracking-wider text-stone-500">Properties</p>
                      <p className="text-xl font-bold text-[#1A4D2E]" style={{fontFamily: '"Playfair Display", serif'}}>Manage</p>
                    </div>
                  </div>
                  <Plus className="text-[#1A4D2E]" size={24} />
                </div>
                {agent?.status === 'pending' && (
                  <p className="text-xs text-yellow-600 mt-2">⚠️ Requires approval</p>
                )}
              </Card>

              <Card 
                className={`bg-white p-6 rounded-2xl card-shadow transition-all ${
                  agent?.status === 'pending' 
                    ? 'opacity-50 cursor-not-allowed' 
                    : 'cursor-pointer hover:card-shadow-hover'
                }`}
                onClick={() => agent?.status !== 'pending' && navigate('/events/create')}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-3 rounded-full bg-green-100">
                      <Calendar className="text-green-600" size={24} />
                    </div>
                    <div>
                      <p className="text-sm font-semibold uppercase tracking-wider text-stone-500">Open Houses</p>
                      <p className="text-xl font-bold text-[#1A4D2E]" style={{fontFamily: '"Playfair Display", serif'}}>Create New</p>
                    </div>
                  </div>
                  <Plus className="text-[#1A4D2E]" size={24} />
                </div>
                {agent?.status === 'pending' && (
                  <p className="text-xs text-yellow-600 mt-2">⚠️ Requires approval</p>
                )}
              </Card>
            </div>

            {/* Open Houses List */}
            <Card className="bg-white p-8 rounded-2xl card-shadow">
              <h2 className="text-2xl font-bold text-[#1A4D2E] mb-6" style={{fontFamily: '"Playfair Display", serif'}}>
                Your Open Houses
              </h2>
              
              {myEvents.length === 0 ? (
                <div className="text-center py-12">
                  <Home className="mx-auto mb-4 text-stone-300" size={48} />
                  <p className="text-lg text-stone-600 mb-4">No open houses yet</p>
                  <p className="text-sm text-stone-500 mb-6">
                    {agent?.status === 'pending' 
                      ? 'Your account needs approval before you can create open houses' 
                      : 'Create properties and open houses to get started'}
                  </p>
                  {agent?.status !== 'pending' && (
                    <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
                      <Button
                        onClick={() => navigate('/broker/properties')}
                        variant="outline"
                        className="border-[#1A4D2E] text-[#1A4D2E] hover:bg-[#1A4D2E] hover:text-white rounded-full px-6"
                      >
                        <MapPin className="mr-2" size={18} />
                        Manage Properties
                      </Button>
                      <Button
                        onClick={() => navigate('/events/create')}
                        className="bg-[#1A4D2E] text-white rounded-full px-6 hover:bg-[#143D24]"
                      >
                        <Plus className="mr-2" size={18} />
                        Create Open House
                      </Button>
                    </div>
                  )}
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {myEvents.map((event) => (
                    <Card
                      key={event.id}
                      onClick={() => navigate(`/events/${event.id}`)}
                      className="bg-white p-6 rounded-2xl card-shadow hover:card-shadow-hover hover:-translate-y-1 transition-all duration-300 cursor-pointer group"
                    >
                      <div className="flex items-start justify-between mb-4">
                        <div className="p-3 rounded-full bg-[#1A4D2E]/10 group-hover:bg-[#1A4D2E]/20 transition-colors">
                          <Home className="text-[#1A4D2E]" size={24} />
                        </div>
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                          event.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-stone-100 text-stone-600'
                        }`}>
                          {event.status}
                        </span>
                      </div>
                      
                      <h3 className="text-xl font-bold text-[#1A4D2E] mb-3 line-clamp-2" style={{fontFamily: '"Playfair Display", serif'}}>
                        {event.property_address}
                      </h3>
                      
                      <div className="space-y-2 text-sm text-stone-600">
                        <div className="flex items-center gap-2">
                          <Calendar size={16} />
                          <span>{(() => { const [y,m,d] = event.date.split('-'); return new Date(y, m-1, d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }); })()}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Clock size={16} />
                          <span>{event.time}{event.end_time ? ` - ${event.end_time}` : ''}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Users size={16} />
                          <span>{event.visitor_count} visitors</span>
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              )}
            </Card>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default BrokerDashboard;
